import { Link } from 'react-router-dom';
import { Card, cx } from '@timely/ui';
import { titled, useDocumentTitle } from '../lib/documentTitle';

/**
 * The frame every signed-out screen sits in.
 *
 * Timely Payment is the only brand here. A company's own name and logo appear
 * once somebody is inside a company — never before, because at this point we do
 * not know which company they belong to, and guessing from an email domain
 * would be a way of telling a stranger who our customers are.
 */
export function AuthLayout({
    title,
    lede,
    children,
    footer,
}: {
    title: string;
    lede?: React.ReactNode;
    children: React.ReactNode;
    footer?: React.ReactNode;
}) {
    useDocumentTitle(titled(title));
    return (
        <main className="min-h-dvh flex flex-col items-center justify-center px-4 py-10">
            <div className="w-full max-w-[420px]">
                <div className="flex items-center gap-2.5 mb-6">
                    <span
                        className="w-9 h-9 rounded-[10px] bg-accent text-on-accent grid place-items-center font-bold text-[14px]"
                        aria-hidden="true"
                    >
                        TP
                    </span>
                    <span className="font-bold text-label tracking-[-0.02em] text-[17px]">
                        Timely Payment
                    </span>
                </div>

                <Card className="px-7 py-8 max-md:px-5">
                    <h1 className="text-[22px] font-bold text-label tracking-[-0.02em] leading-tight">
                        {title}
                    </h1>
                    {lede && (
                        <p className="text-[14px] text-label-2 mt-2.5 leading-relaxed">{lede}</p>
                    )}
                    <div className="mt-6">{children}</div>
                </Card>

                {footer && <div className="text-[13.5px] text-label-2 mt-5 text-center">{footer}</div>}
            </div>
        </main>
    );
}

/** A labelled field. The label is a real one, not a placeholder standing in. */
export function Field({
    label,
    hint,
    error,
    ...input
}: React.InputHTMLAttributes<HTMLInputElement> & {
    label: string;
    hint?: React.ReactNode;
    error?: string | null;
}) {
    const id = input.id ?? input.name ?? label.toLowerCase().replace(/\s+/g, '-');
    const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
        .filter(Boolean)
        .join(' ');
    return (
        <div className="mb-4">
            <label htmlFor={id} className="block text-[13px] font-semibold text-label mb-1.5">
                {label}
            </label>
            <input
                {...input}
                id={id}
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy || undefined}
                className={cx(
                    'w-full h-11 px-3.5 rounded-[11px] bg-card border text-[15px] text-label',
                    'outline-none transition-colors',
                    error ? 'border-dang' : 'border-separator-strong focus:border-accent',
                    input.className,
                )}
            />
            {hint && (
                <div id={`${id}-hint`} className="text-[12.5px] text-label-3 mt-1.5 leading-snug">
                    {hint}
                </div>
            )}
            {error && (
                <div id={`${id}-error`} className="text-[12.5px] text-dang mt-1.5" role="alert">
                    {error}
                </div>
            )}
        </div>
    );
}

/** A message that concerns the whole form rather than one field. */
export function FormNotice({ tone, children }: { tone: 'error' | 'ok'; children: React.ReactNode }) {
    return (
        <div
            role={tone === 'error' ? 'alert' : 'status'}
            className={cx(
                'rounded-[11px] px-3.5 py-3 text-[13.5px] leading-relaxed mb-4',
                tone === 'error' ? 'bg-dang-bg text-dang' : 'bg-pos-bg text-pos',
            )}
        >
            {children}
        </div>
    );
}

export const AuthLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
    <Link to={to} className="text-accent font-semibold hover:underline">
        {children}
    </Link>
);
